import { useMemo } from 'react';
import type { EnquiryAudienceEnum } from 'generated/graphql';
import { useUser } from '~/modules/user/hook/use-user';
import {
    getEnquiryVisibility,
    type EnquiryVisibilityResult,
} from '../util/get-enquiry-visibility';

/**
 * Hook to determine the visibility of enquiry CTAs and forms based on the
 * professional's enquiry audience setting and the current user's classification.
 *
 * @param professional - The professional's enquiry settings.
 * @returns The visibility result for enquiry elements.
 *
 * @example
 * ```tsx
 * const { showEnquiryCTA, showEnquiryForm, showSpecifierLoginPrompt } =
 *     useEnquiryVisibility(product.Professional);
 *
 * return (
 *     <>
 *         {showEnquiryCTA && <EnquiryButton />}
 *         {showEnquiryForm && <EnquiryForm />}
 *         {showSpecifierLoginPrompt && <SpecifierLoginPrompt />}
 *     </>
 * );
 * ```
 */
export function useEnquiryVisibility({
    EnquiryAudience,
}: {
    /** The professional's enquiry audience setting. */
    EnquiryAudience: EnquiryAudienceEnum;
}): EnquiryVisibilityResult {
    const user = useUser();

    return useMemo(() => {
        return getEnquiryVisibility({
            enquiryAudience: EnquiryAudience,
            user,
        });
    }, [EnquiryAudience, user]);
}
